/**
 * A table, flattened so it can cross a thread boundary.
 *
 * The analysis runs in a Web Worker (see `decide.ts` for why), and a `BlackjackTable`
 * cannot be posted: it holds a shoe, a seed and a draw pointer, none of which the
 * analysis is allowed to see anyway. So the room sends a snapshot of what is FACE UP
 * plus the flags the rules depend on, and the worker rebuilds a table that answers
 * `composition()` and `legalActions()` identically.
 *
 * Two properties matter and are tested in `blackjack-snapshot.test.ts`:
 *
 *   IT CARRIES NO HIDDEN INFORMATION. The dealer's hole card is not in here. A snapshot
 *   is exactly the player's view, which is the only view the analysis is entitled to.
 *
 *   IT IS FAITHFUL ON THE FLAGS. An earlier version inferred `fromSplit` from the hand
 *   count and dropped `splitAces` and `doubled` entirely, which would have let the
 *   worker offer a hit on a split ace — an action the real table refuses. Flags are
 *   copied, never re-derived.
 */
import { BlackjackTable, type Phase, type PlayerHand } from "./table";
import type { BlackjackRules } from "./rules";

export interface HandSnapshot {
  cards: number[];
  bet: number;
  doubled: boolean;
  fromSplit: boolean;
  splitAces: boolean;
  done: boolean;
}

export interface TableSnapshot {
  phase: Phase;
  /** Face-up dealer cards only. Length is 1 before the dealer plays. */
  dealer: number[];
  dealerNatural: boolean | null;
  turn: { seat: number; hand: number } | null;
  seats: { isHero: boolean; insurance: number; hands: HandSnapshot[] }[];
}

const face = (h: PlayerHand): HandSnapshot => ({
  cards: [...h.cards],
  bet: h.bet,
  doubled: h.doubled,
  fromSplit: h.fromSplit,
  splitAces: h.splitAces,
  done: h.done,
});

/** The player's view of the table, as plain data. */
export function snapshot(t: BlackjackTable): TableSnapshot {
  const showAll = t.phase === "dealer" || t.phase === "settled";
  return {
    phase: t.phase,
    dealer: showAll ? [...t.dealer] : t.dealer.slice(0, 1),
    dealerNatural: t.dealerNatural,
    turn: t.turn ? { ...t.turn } : null,
    seats: t.seats.map((s) => ({
      isHero: s.isHero,
      insurance: s.insurance,
      hands: s.hands.map(face),
    })),
  };
}

/**
 * A table that answers the analysis questions the same way.
 *
 * It has no shoe: drawing from it throws. That is intentional — this object exists to
 * be measured, not played, and a rehydrated table that could deal would be a second
 * source of cards with no seed behind it.
 */
export function rehydrate(snap: TableSnapshot, rules: BlackjackRules): BlackjackTable {
  const t = new BlackjackTable({
    rules,
    seats: snap.seats.map((s, i) => ({ id: `s${i}`, isHero: s.isHero })),
  });
  t.phase = snap.phase;
  t.dealer = [...snap.dealer];
  t.dealerNatural = snap.dealerNatural;
  t.turn = snap.turn ? { ...snap.turn } : null;
  snap.seats.forEach((s, i) => {
    const seat = t.seats[i]!;
    seat.insurance = s.insurance;
    seat.hands = s.hands.map((h) => ({
      cards: [...h.cards],
      bet: h.bet,
      doubled: h.doubled,
      fromSplit: h.fromSplit,
      splitAces: h.splitAces,
      done: h.done,
      settled: null,
      outcome: null,
    }));
  });
  return t;
}
